/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, IconButton, Step, StepLabel, Stepper, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { AnalysisRise, AssessementAnomaliesRise, EliminationAnomaliesRise, GeneralRise, HydrologicalCheck, MonitoringRise, ToolsDamInformation } from '../../../components/custom';
import { LayoutBase } from '../../../layouts';
import { ArrowCircleLeft, ArrowCircleRight, CheckCircle } from '@mui/icons-material';
import { AnaliseEstabilidade, Anomalias, IReportsRise, Monitoramentos, Recomendacoes, ReportsServicesRise, VerificacaoHidrologica } from '../../../services/api/reports/ReportsServicesRise';
import { useNavigate, useParams } from 'react-router-dom';
import { CustomAlert } from '../../../components/shared';
import { useAuthContext } from '../../../context';
import HorizontalStepperWithScroll from '../../../components/shared/StepsCarrocel';

const steps = ['GERAL', 'ANÁLISES', 'VERIFICAÇÃO', 'MONITORAMENTOS', 'ANOMALIAS', 'RECOMENDAÇÕES DAS ANOMALIAS'];

interface StepContentProps {
    step: number;
    onSubmit: (data: any) => void;
    setSubmitCallback: (callback: () => void) => void;
    dataRise: IReportsRise;
    handleAnalysis: (data: AnaliseEstabilidade) => void;
    handleHydrologicalChange: (data: VerificacaoHidrologica) => void;
    handleMonitoringChange: (data: Monitoramentos[]) => void;
    handleAnomaliasChange: (data: Anomalias[]) => void;
    handleEliminationChange: (data: Recomendacoes[]) => void;
}

const StepContent = ({ step, onSubmit, setSubmitCallback, dataRise, handleAnalysis, handleHydrologicalChange, handleMonitoringChange, handleAnomaliasChange, handleEliminationChange }: StepContentProps) => {
    switch (step) {
        case 0:
            return <GeneralRise onSubmit={onSubmit} setSubmitCallback={setSubmitCallback} dataRise={dataRise} />;
        case 1:
            return <AnalysisRise data={dataRise.analiseEstabilidade || { informacoes: [], metodo_analise: '', modos_falha: '' }} onAnalysisChange={handleAnalysis} />;
        case 2:
            return <HydrologicalCheck dataRise={dataRise} onDataChange={handleHydrologicalChange} />;
        case 3:
            return <MonitoringRise dataRise={dataRise} onDataChange={handleMonitoringChange} />;
        case 4:
            return <AssessementAnomaliesRise dataRise={dataRise} onDataChange={handleAnomaliasChange} />;
        case 5:
            return <EliminationAnomaliesRise dataRise={dataRise} onDataChange={handleEliminationChange} />;
        default:
            return null;
    }
};

export const RegisterReportsRise = () => {
    const [activeStep, setActiveStep] = useState(0);
    const submitCallbackRef = useRef<() => void>();
    const { userData } = useAuthContext();
    // const permissions = userData.permissoes;

    const { rise } = useParams();
    const { id } = useParams();

    const pagePermissionsString = useMemo(() => {
        return userData?.nivel_acesso?.registros?.RISE;
    }, [userData]);

    const hasActionPermission = useCallback((action: 'add' | 'edit' | 'remove' | 'view'): boolean => {
        if (typeof pagePermissionsString === 'string' && pagePermissionsString !== '') {
            const actions = pagePermissionsString.split(',').map(s => s.trim());
            return actions.includes(action);
        }
        return false;
    }, [pagePermissionsString]);

    const [data, setData] = useState<IReportsRise>({
        id_barragem: '',
        motivo: '',
        empresa_executora: '',
        data_inspecao: '',
        monitoramentos: [],
        recomendacoes: [],
        verificacaoHidrologica: {},
        analiseEstabilidade: { informacoes: [], metodo_analise: '', modos_falha: '' },
        anomalias: [],
    });

    const [alertInfo, setAlertInfo] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const navigate = useNavigate();

    useEffect(() => {
        fetchData();
    }, [rise]);

    const fetchData = () => {
        if (rise === 'new') {
            const newData: IReportsRise = {
                id_barragem: '',
                motivo: '',
                empresa_executora: '',
                data_inspecao: '',
                monitoramentos: [],
                recomendacoes: [],
                verificacaoHidrologica: {},
                analiseEstabilidade: { informacoes: [], metodo_analise: '', modos_falha: '' },
                anomalias: [],
            };
            setData(newData);
        } else {
            if (rise) {
                ReportsServicesRise.get(rise)
                    .then(result => {
                        if (result instanceof Error) {
                            console.log(result.message);
                        } else {
                            setData({
                                ...result,
                                analiseEstabilidade: {
                                    informacoes: result.analiseEstabilidade?.informacoes || [],
                                    metodo_analise: result.analiseEstabilidade?.metodo_analise || '',
                                    modos_falha: result.analiseEstabilidade?.modos_falha || '',
                                }
                            });
                        }
                    });
            }
        }
    };

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1);
    };

    const handleNext = useCallback(() => {
        if (submitCallbackRef.current) {
            submitCallbackRef.current();
        } else {
            setActiveStep(prevActiveStep => prevActiveStep + 1);
        }
    }, []);

    const handleFormSubmit = useCallback((formData: Partial<IReportsRise>) => {
        setData(prevData => {
            if (prevData) {
                return {
                    ...prevData,
                    ...formData,
                };
            }
            return prevData;
        });
        setActiveStep(prevActiveStep => prevActiveStep + 1);
    }, [setData]);

    const handleAnalysisChange = useCallback((newAnalysisData: AnaliseEstabilidade) => {
        setData(prevData => {
            if (prevData) {
                return {
                    ...prevData,
                    analiseEstabilidade: newAnalysisData,
                };
            }
            return prevData;
        });
    }, [setData]);

    const handleHydrologicalChange = useCallback((newHydrologicalData: VerificacaoHidrologica) => {
        setData(prevData => {
            if (prevData) {
                return {
                    ...prevData,
                    verificacaoHidrologica: newHydrologicalData,
                };
            }
            return prevData;
        });
    }, [setData]);

    const handleMonitoringChange = useCallback((newMonitoring: Monitoramentos[]) => {
        setData(prevData => {
            if (prevData) {
                return {
                    ...prevData,
                    monitoramentos: newMonitoring,
                };
            }
            return prevData;
        });
    }, [setData]);

    const handleAnomaliasChange = useCallback((newAnomalias: Anomalias[]) => {
        setData(prevData => {
            if (prevData) {
                return {
                    ...prevData,
                    anomalias: newAnomalias,
                };
            }
            return prevData;
        });
    }, [setData]);

    const handleEliminationChange = useCallback((newElimination: Recomendacoes[]) => {
        setData(prevData => {
            if (prevData) {
                return {
                    ...prevData,
                    recomendacoes: newElimination,
                };
            }
            return prevData;
        });
    }, [setData]);

    const setSubmitCallback = useCallback((callback: () => void) => {
        submitCallbackRef.current = callback;
    }, []);

    const handleSave = useCallback(async () => {
        const isEditing = rise !== 'new';
        const canAdd = hasActionPermission('add');
        const canEdit = hasActionPermission('edit');

        if ((!isEditing && !canAdd) || (isEditing && !canEdit)) {
            setAlertInfo({ open: true, message: 'Verifique seu nível de acesso!', severity: 'warning' });
            return;
        }

        if (data !== undefined && id !== undefined) {
            const dataToApi = data;
            dataToApi.id_barragem = id;

            if (rise === 'new') {
                const response = await ReportsServicesRise.create(dataToApi);
                if (response instanceof Error) {
                    setAlertInfo({ open: true, message: 'Erro ao salvar relatório!', severity: 'error' });
                } else {
                    setAlertInfo({ open: true, message: 'Relatório salvo!', severity: 'success' });
                    setTimeout(() => {
                        navigate(`/information_dam/${id}/rise`);
                    }, 2000);
                }
            } else {
                if (rise !== undefined) {
                    const response = await ReportsServicesRise.upgrade(dataToApi, rise);
                    if (response instanceof Error) {
                        setAlertInfo({ open: true, message: `Erro ao salvar relatório: ${response.message}`, severity: 'error' });
                    } else {
                        setAlertInfo({ open: true, message: 'Relatório salvo!', severity: 'success' });
                        setTimeout(() => {
                            navigate(`/information_dam/${id}/rise`);
                        }, 2000);
                    }
                }
            }
        }
    }, [hasActionPermission, data, id, rise, navigate]);

    const handleNextStep = useCallback(() => {
        if (activeStep === steps.length - 1) {
            handleSave();
        } else {
            handleNext();
        }
    }, [activeStep, steps.length, handleSave, handleNext]);

    return (
        <LayoutBase
            title='Relatório Inspeção de Segurança Especial (RISE)'
            tollBar={<ToolsDamInformation />}
        >
            <Box>
                <Box sx={{ width: '100%' }}>
                    <HorizontalStepperWithScroll activeStep={activeStep} steps={steps} />
                </Box>
                <Box sx={{ marginTop: 2 }} width={'90%'} padding={1} marginLeft={1}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                        <IconButton
                            color="inherit"
                            disabled={activeStep === 0}
                            onClick={handleBack}
                        >
                            <ArrowCircleLeft sx={{ height: 40, width: 40 }} />
                        </IconButton>

                        <Box sx={{ flex: '1 1 auto' }} />
                        {activeStep === steps.length - 1 ? (
                            <IconButton
                                onClick={handleNextStep}
                                disabled={
                                    (rise === 'new' && !hasActionPermission('add')) ||
                                    (rise !== 'new' && !hasActionPermission('edit'))
                                }
                            >
                                <CheckCircle sx={{ height: 40, width: 40 }} />
                            </IconButton>
                        ) : (
                            <IconButton onClick={handleNext}><ArrowCircleRight sx={{ height: 40, width: 40 }} /></IconButton>
                        )}
                    </Box>
                    <StepContent
                        step={activeStep}
                        onSubmit={handleFormSubmit}
                        setSubmitCallback={setSubmitCallback}
                        dataRise={data}
                        handleAnalysis={handleAnalysisChange}
                        handleHydrologicalChange={handleHydrologicalChange}
                        handleMonitoringChange={handleMonitoringChange}
                        handleAnomaliasChange={handleAnomaliasChange}
                        handleEliminationChange={handleEliminationChange}
                    />
                </Box>
            </Box>
            {alertInfo.open &&
                <CustomAlert
                    severity={alertInfo.severity}
                    message={alertInfo.message}
                />
            }
        </LayoutBase>
    );
};